import React, { useState, useEffect, useContext } from "react";
import { useParams } from 'react-router-dom';
import { ApiContext } from '../App';
import { Users, AlertCircle, Mail, Phone, Copy, Check, Briefcase } from 'lucide-react';
import { FaLinkedin } from 'react-icons/fa';

// Styles pour le wrapper
const wrapperStyle = {
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  paddingBottom: "16px",
  background: "#f5f5f5",
  overflowX: "auto",
  minHeight: "calc(100vh - 150px)",
  borderRadius: "0.5rem"
};

// Composant avec icône de copie au survol
const CopyableInfo = ({ children, textToCopy, showTooltip = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = React.useRef(null);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (containerRef.current && showTooltip) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 8,
        left: rect.left
      });
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        className="relative group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center gap-1 w-full">
          <div className="flex-1 min-w-0">
            {children}
          </div>
          {isHovered && (
            <button
              onClick={handleCopy}
              className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
              title={copied ? "Copié !" : "Copier"}
            >
              {copied ? (
                <Check size={12} className="text-green-600" />
              ) : (
                <Copy size={12} className="text-gray-400" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Tooltip au survol - rendu en position fixe pour éviter d'être coupé */}
      {showTooltip && isHovered && !copied && (
        <div
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-100%)',
            zIndex: 9999
          }}
          className="px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap pointer-events-none"
        >
          {textToCopy}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </>
  );
};

const SharedProcessBoard = () => {
  const { shareToken } = useParams();
  const API = useContext(ApiContext);
  const [positionData, setPositionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidatesByStage, setCandidatesByStage] = useState({});

  // Récupération des données via le token de partage
  useEffect(() => {
    const fetchSharedPosition = async () => {
      try {
        console.log('🔍 Fetching shared position with token:', shareToken);
        console.log('🔍 API baseURL:', API.defaults?.baseURL);
        console.log('🔍 Full constructed URL:', `${API.defaults?.baseURL}/api/positions/shared/${shareToken}`);

        const response = await API.get(`/api/positions/shared/${shareToken}`);

        console.log('✅ Response received:', response.data);
        setPositionData(response.data);

        // Grouper les candidats par étape
        const activeSteps = response.data.steps.filter(step => step.active);
        const grouped = response.data.candidates.reduce((acc, candidate) => {
          let stageId;

          // Priorité à stageId si disponible
          if (candidate.stageId) {
            const step = activeSteps.find(s => s._id === candidate.stageId);
            stageId = step ? step._id : null;
          }

          // Fallback sur stage (nom) pour compatibilité
          if (!stageId) {
            const step = activeSteps.find(s => s.name === candidate.stage);
            stageId = step ? step._id : activeSteps[0]?._id;
          }

          if (stageId) {
            if (!acc[stageId]) acc[stageId] = [];
            acc[stageId].push(candidate);
          }

          return acc;
        }, {});

        setCandidatesByStage(grouped);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error fetching shared position:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response,
          status: err.response?.status,
          data: err.response?.data
        });
        setError(err.response?.data?.error || 'Erreur lors du chargement de la position partagée');
        setLoading(false);
      }
    };

    fetchSharedPosition();
  }, [shareToken, API]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la position...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur d'accès</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Vérifiez que le lien est correct et n'a pas expiré.</p>
        </div>
      </div>
    );
  }

  // Statistiques globales
  const totalCandidates = positionData.candidates.length;
  const activeSteps = positionData.steps.filter(step => step.active);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header avec branding */}
      <div className="bg-white border-b border-gray-200">
        <div className="mx-auto px-4 sm:px-2 lg:px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-indigo-50 p-2 border rounded-lg">
                  <Briefcase className="w-6 h-6 text-indigo-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900 m-0">{positionData.title}</h1>
              </div>

              <div className="bg-white rounded-lg mt-2">
                <div className="flex items-center">
                  <div>
                    <Users className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <p className="text-sm font-medium text-gray-600">Total candidats : </p>
                    <p className="text-base font-bold text-gray-900">{totalCandidates}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau Kanban */}
        <div className="bg-white rounded-lg">
          <div style={wrapperStyle} className="p-4">
            {activeSteps.map((step) => {
              const candidatesInStep = candidatesByStage[step._id] || [];

              return (
                <div
                  key={step._id}
                  className="bg-white rounded-lg p-4 flex flex-col min-w-[280px] max-w-[320px]"
                >
                  {/* Header de la colonne */}
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900 uppercase text-sm m-0">
                      {step.name}
                    </h3>
                    <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                      {candidatesInStep.length}
                    </span>
                  </div>

                  {/* Liste des candidats */}
                  <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-400px)]">
                    {candidatesInStep.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm">
                        Aucun candidat
                      </div>
                    ) : (
                      candidatesInStep.map((candidate) => (
                        <div
                          key={candidate._id}
                          className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start gap-3">
                            {/* Photo */}
                            {candidate.individual?.photo ? (
                              <img
                                src={candidate.individual.photo}
                                alt={candidate.individual.Name}
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                <Users size={20} className="text-gray-500" />
                              </div>
                            )}

                            {/* Informations */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <p className="font-medium text-gray-900 text-sm truncate">
                                  {candidate.individual?.Name || 'N/A'}
                                </p>
                                {candidate.individual?.linkedinUrl && (
                                  <a
                                    href={candidate.individual.linkedinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                                    title="Voir le profil LinkedIn"
                                  >
                                    <FaLinkedin size={16} />
                                  </a>
                                )}
                              </div>

                              {/* Email professionnel */}
                              {candidate.individual?.professionalEmail && (
                                <CopyableInfo textToCopy={candidate.individual.professionalEmail}>
                                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                                    <Mail size={12} className="text-blue-500 flex-shrink-0" />
                                    <span className="truncate">{candidate.individual.professionalEmail}</span>
                                  </div>
                                </CopyableInfo>
                              )}

                              {/* Email personnel */}
                              {candidate.individual?.personalEmail && (
                                <CopyableInfo textToCopy={candidate.individual.personalEmail}>
                                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                                    <Mail size={12} className="text-green-500 flex-shrink-0" />
                                    <span className="truncate">{candidate.individual.personalEmail}</span>
                                  </div>
                                </CopyableInfo>
                              )}

                              {/* Téléphone */}
                              {candidate.individual?.phoneNumber && (
                                <CopyableInfo textToCopy={candidate.individual.phoneNumber} showTooltip={false}>
                                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                                    <Phone size={12} className="text-gray-500 flex-shrink-0" />
                                    <span className="truncate">{candidate.individual.phoneNumber}</span>
                                  </div>
                                </CopyableInfo>
                              )}

                              <p className="text-xs text-gray-400 mt-2">
                                {candidate.stageHistory && candidate.stageHistory.length > 0
                                  ? `Dans cette étape depuis le ${new Date(candidate.stageHistory[candidate.stageHistory.length - 1].date).toLocaleDateString('fr-FR')}`
                                  : `Ajouté le ${new Date(candidate.addedAt).toLocaleDateString('fr-FR')}`
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-500">
            Cette page est en lecture seule et a été partagée à titre informatif
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedProcessBoard;
